<template>
  <div
    :class="$register ? 'row' : 'column q-gutter-y-sm'"
    data-test-id="donation-tip-buttons"
  >
    <template v-if="type === DONATION_TIP_TYPES.PERCENT">
      <q-btn-toggle
        :model-value="percentage"
        class="bordered content-stacked"
        :class="{
          [textColor]: true,
          'col-auto': $register,
          'col': !$register,
          'q-gutter-md': $q.screen.gt.xs && !$register,
          'q-gutter-x-sm': $q.screen.xs || $register
        }"
        :style="{
          'max-width': $register ? '320px' : '100%',
          '--toggle-color': buttonColor === 'custom' ? buttonColorHex : undefined
        }"
        :size="isMobileView || $register || checkout ? 'md' : 'lg'"
        color="transparent"
        :text-color="textColor"
        :toggle-color="buttonColor"
        :toggle-text-color="toggleTextColor"
        unelevated
        spread
        :options="percentOptions"
        @update:model-value="value => updatePercentage(value)"
      >
        <template
          v-for="option in $_.filter(percentOptions, option => option.value && option.value > 0)"
          :key="option.value"
          #[option.value]
        >
          <div
            class="text-caption"
            :class="toggleTextColor"
            style="opacity: 0.8;"
          >
            (<i18n-n
              :value="computeAmountFromPercent(option.value)"
              format="currency"
              scope="global"
            />)
          </div>
        </template>
      </q-btn-toggle>
    </template>
    <template v-else-if="DONATION_TIP_TYPES.AMOUNT">
      <q-btn-toggle
        :model-value="amount"
        class="col q-gutter-md bordered justify-center items-center"
        :class="{
          [textColor]: true,
          'col-auto': $register,
          'col': !$register,
          'q-gutter-md': $q.screen.gt.xs,
          'q-gutter-x-sm': $q.screen.xs
        }"
        style="min-height: 60px;"
        :style="{
          'max-width': $register ? '320px' : undefined,
          '--toggle-color': buttonColor === 'custom' ? buttonColorHex : undefined
        }"
        :size="isMobileView || $register || checkout ? 'md' : 'lg'"
        color="transparent"
        :text-color="textColor"
        :toggle-color="buttonColor"
        :toggle-text-color="toggleTextColor"
        unelevated
        spread
        :dense="isMobileView"
        :options="amountOptions"
        @update:model-value="value => updateAmount(value)"
      />
    </template>
    <div
      :class="{
        'row justify-center items-center': inlineSubmitButton,
        'column': !inlineSubmitButton,
        'col q-ml-sm': $register
      }"
    >
      <q-input
        :model-value="amount"
        class="text-bold col custom-amount"
        :class="{ [`border-${buttonColor}`]: !inlineSubmitButton }"
        :style="{ '--toggle-color': buttonColor === 'custom' && !inlineSubmitButton ? buttonColorHex : undefined }"
        style="font-size: 18px;"
        :label="$t('pricing.custom')"
        type="number"
        step="1"
        min="0"
        debounce="100"
        outlined
        :dark="(!textColor || textColor === 'responsive') ? $q.dark.isActive : textColor === 'white'"
        @update:model-value="value => updateAmount($_.isEmpty(value) ? 0 : parseFloat(value))"
      >
        <template #prepend>
          <span
            class="text-weight-light"
          >{{ currentCircuit.localeCurrencySymbol }}</span>
        </template>
        <template #append>
          <q-icon
            v-if="amount !== 0"
            name="cancel"
            class="cursor-pointer"
            @click.stop.prevent="updateAmount(0)"
          />
          <q-chip v-if="!$register">
            {{ currentCircuit.currency }}
          </q-chip>
        </template>
      </q-input>
      <slot name="submit-button" />
    </div>
  </div>
</template>

<script>
import { region } from 'src/config'
import { DONATION_TIP_TYPES } from 'src/models/site/Site'
export default {
  name: 'DonationTipButtons',
  configuration: ['currentCircuit', 'currentSite'],

  props: {
    amount: {
      type: Number,
      required: true
    },
    percentage: {
      type: Number,
      required: true
    },
    isMobileView: {
      type: Boolean,
      required: false,
      default: false
    },
    percentOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    amountOptions: {
      type: Array,
      required: false,
      default: () => []
    },
    type: {
      type: String,
      required: false,
      default: DONATION_TIP_TYPES.AMOUNT
    },
    checkout: {
      type: Boolean,
      required: false,
      default: false
    },

    orderTotalWithoutDonationsOrTips: {
      type: Number,
      required: true
    },
    inlineSubmitButton: {
      type: Boolean,
      required: false,
      default: false
    },
    buttonColor: {
      type: String,
      required: false,
      default: 'primary'
    },
    buttonColorHex: {
      type: String,
      required: false,
      default: '#FFFFFF'
    },
    textColor: {
      type: String,
      required: false,
      default: 'responsive'
    },
    maxWidth: {
      type: String,
      required: false,
      default: null
    }
  },
  emits: ['update:amount', 'update:percentage'],
  computed: {
    toggleTextColor () {
      let colorHex

      if (this.buttonColor === 'custom') {
        colorHex = this.buttonColorHex
      } else {
        colorHex = this.$getSiteColorHex(this.currentSite, this.buttonColor)
      }
      return this.$isColorDark(colorHex) ? 'white' : 'black'
    }
  },
  watch: {
    orderTotalWithoutDonationsOrTips (value) {
      if (value <= 0) {
        this.updateAmount(0)
      } else {
        this.updatePercentage(this.percentage)
      }
    }
  },
  created () {
    _.extend(this, { DONATION_TIP_TYPES, region })
  },
  unmounted () {
    this.stopWatch && this.stopWatch()
  },
  methods: {
    updateAmount (value) {
      this.$emit('update:amount', value)
      this.$emit('update:percentage', this.computePercentFromAmount(value))
    },
    updatePercentage (value) {
      this.$emit('update:percentage', value)
      this.$emit('update:amount', this.computeAmountFromPercent(value))
    },
    computeAmountFromPercent (percent) {
      return Math.round(this.orderTotalWithoutDonationsOrTips * percent) / 100.0
    },
    computePercentFromAmount (amount) {
      return Math.round((amount / this.orderTotalWithoutDonationsOrTips) * 100.0)
    }
  }

}

</script>

<style lang="scss" scoped>
.q-btn-group--unelevated.bordered :deep(.q-btn){
  border-radius: 4px;
}
.body--light .q-btn-group--unelevated.bordered.responsive :deep(.q-btn.bg-transparent),
.q-btn-group--unelevated.bordered.black :deep(.q-btn.bg-transparent) {
  border: solid 1px rgba(0, 0, 0, 0.2);
}
.body--dark .q-btn-group--unelevated.bordered.responsive :deep(.q-btn.bg-transparent),
.q-btn-group--unelevated.bordered.white :deep(.q-btn.bg-transparent){
  border: solid 1px rgba(255, 255, 255, 0.2);
}
.q-btn-group :deep(.q-btn.bg-custom) {
  background: var(--toggle-color);
}
.content-stacked {
  min-height: 50px;
}
.content-stacked :deep(.q-btn > .q-btn__content) {
  display: block;
}
.q-field--outlined.border-primary :deep(.q-field__control:before) {
  border: 2px solid var(--q-primary);
}
.q-field--outlined.border-secondary :deep(.q-field__control:before) {
  border: 2px solid var(--q-secondary);
}
.q-field--outlined.border-header :deep(.q-field__control:before) {
  border: 2px solid var(--q-header);
}
.q-field--outlined.border-accent :deep(.q-field__control:before) {
  border: 2px solid var(--q-accent);
}
.q-field--outlined.border- :deep(.q-field__control:before) {
  border: 2px solid var(--q-dark);
}
.q-field--outlined.border-positive :deep(.q-field__control:before) {
  border: 2px solid var(--q-positive);
}
.q-field--outlined.border-negative :deep(.q-field__control:before) {
  border: 2px solid var(--q-negative);
}
.q-field--outlined.border-info :deep(.q-field__control:before) {
  border: 2px solid var(--q-info);
}
.q-field--outlined.border-warning :deep(.q-field__control:before){
  border: 2px solid var(--q-warning);
}
.q-field--outlined.border-custom :deep(.q-field__control:before) {
  border: 2px solid var(--toggle-color);
}

.custom-amount :deep(.q-field__label){
  left: -22px;
  top: 14px;
}

.q-field--labeled.custom-amount :deep(.q-field__native) {
    padding-top: 20px;
}

.custom-amount :deep(.q-field__prepend .text-weight-light) {
  font-size: 20px;
  padding-top: 12px;
}

</style>
